
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserApps } from '@/hooks/useApps';

export interface Chemical {
  id: string;
  name: string;
  cas_number: string | null;
  chemical_type: string | null;
  source?: string;
}

// Mock data to use when the chemicals table doesn't exist
const MOCK_CHEMICALS: Chemical[] = [
  { id: '1', name: 'Sodium Chloride', cas_number: '7647-14-5', chemical_type: 'Salt', source: 'Standardly' },
  { id: '2', name: 'Acetone', cas_number: '67-64-1', chemical_type: 'Solvent', source: 'Standardly' },
  { id: '3', name: 'Calcium Carbonate', cas_number: '471-34-1', chemical_type: 'Mineral', source: 'Standardly' },
  { id: '4', name: 'Sodium Hydroxide', cas_number: '1310-73-2', chemical_type: 'Base', source: 'Standardly' },
  { id: '5', name: 'Hydrochloric Acid', cas_number: '7647-01-0', chemical_type: 'Acid', source: 'Standardly' },
  { id: '6', name: 'Ethanol', cas_number: '64-17-5', chemical_type: 'Alcohol', source: 'Standardly' },
  { id: '7', name: 'Benzene', cas_number: '71-43-2', chemical_type: 'Aromatic Hydrocarbon', source: 'Standardly' },
  { id: '8', name: 'Glycerol', cas_number: '56-81-5', chemical_type: 'Alcohol', source: 'Standardly' },
  { id: '9', name: 'Citric Acid', cas_number: '77-92-9', chemical_type: 'Organic Acid', source: 'Standardly' },
  { id: '10', name: 'Hydrogen Peroxide', cas_number: '7722-84-1', chemical_type: 'Oxidizer', source: 'Standardly' },
];

// Green Seal specific chemicals (mock data)
const GREEN_SEAL_CHEMICALS: Chemical[] = [
  { id: 'gs1', name: 'Plant-based Surfactant', cas_number: '68439-57-6', chemical_type: 'Surfactant', source: 'Green Seal' },
  { id: 'gs2', name: 'Biodegradable Detergent', cas_number: '9004-82-4', chemical_type: 'Cleaning Agent', source: 'Green Seal' },
  { id: 'gs3', name: 'Natural Citrus Extract', cas_number: '8028-48-6', chemical_type: 'Natural Extract', source: 'Green Seal' },
  { id: 'gs4', name: 'Coconut-derived Cleanser', cas_number: '61789-18-2', chemical_type: 'Bio-surfactant', source: 'Green Seal' },
  { id: 'gs5', name: 'Enzyme-based Cleaner', cas_number: '9014-01-1', chemical_type: 'Enzyme', source: 'Green Seal' },
];

export const useChemicalSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { userApps } = useUserApps();

  // Check if Green Seal app is installed and enabled
  const greenSealApp = userApps.find(
    ua => ua.app?.name === 'green-seal' && ua.is_enabled
  );

  const searchChemicals = async (term: string): Promise<Chemical[]> => {
    if (!term || term.length < 2) return [];
    
    try {
      // If Green Seal app is enabled, prioritize Green Seal chemicals
      if (greenSealApp) {
        console.log('Using Green Seal chemical database');
        const greenSealResults = GREEN_SEAL_CHEMICALS.filter(chemical => 
          chemical.name.toLowerCase().includes(term.toLowerCase())
        );
        const standardResults = MOCK_CHEMICALS.filter(chemical => 
          chemical.name.toLowerCase().includes(term.toLowerCase())
        );
        return [...greenSealResults, ...standardResults];
      }
      
      // Default to standard chemical database
      return MOCK_CHEMICALS.filter(chemical => 
        chemical.name.toLowerCase().includes(term.toLowerCase())
      );
    } catch (err) {
      console.warn('Error searching chemicals, using mock data:', err);
      return MOCK_CHEMICALS.filter(chemical => 
        chemical.name.toLowerCase().includes(term.toLowerCase())
      );
    }
  };

  const { data: results, isLoading, error } = useQuery({
    queryKey: ['chemicals', searchTerm, greenSealApp?.id],
    queryFn: () => searchChemicals(searchTerm),
    enabled: searchTerm.length >= 2,
  });

  return {
    searchTerm,
    setSearchTerm,
    results: results || [],
    isLoading,
    error,
    usingGreenSeal: !!greenSealApp
  };
};
